import type {
  ContentPage,
  ContentSection,
  GeniusContent,
  NavItem,
  OlympiadEntry,
} from "./types";

function getNestedValue(root: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (!acc || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[key];
  }, root);
}

export function resolveSection(
  content: GeniusContent,
  section: ContentSection
): ContentSection {
  if (!section.useShared) return section;

  const sharedData = getNestedValue(content.shared as Record<string, unknown>, section.useShared);

  if (!sharedData || typeof sharedData !== "object") return section;

  const { id, useShared, ...rest } = section;
  return {
    id,
    ...(sharedData as Record<string, unknown>),
    ...rest,
  };
}

export function getPage(content: GeniusContent, pageId: string): ContentPage | undefined {
  return content.pages.find((page) => page.id === pageId);
}

export function getPageSection(
  content: GeniusContent,
  pageId: string,
  sectionId: string
): ContentSection | undefined {
  const page = getPage(content, pageId);
  if (!page) return undefined;

  const section = page.sections.find((s) => s.id === sectionId);
  if (!section) return undefined;

  return resolveSection(content, section);
}

export function getSharedSection(content: GeniusContent, key: string): Record<string, unknown> {
  const data = content.shared[key];
  return data && typeof data === "object" ? (data as Record<string, unknown>) : {};
}

export function buildNavItems(content: GeniusContent): NavItem[] {
  return content.navigation.items.map((item) => {
    if (item.id !== "olympiad" || !item.hasSubMenu) return item;

    return {
      ...item,
      subItems: content.olympiads.map((olympiad) => ({
        id: olympiad.id,
        label: olympiad.navLabel,
        href: olympiad.route,
      })),
    };
  });
}

export function getOlympiadPageSections(
  content: GeniusContent,
  olympiadId: string
): ContentSection[] {
  const page = getPage(content, olympiadId);
  if (!page) return [];

  return page.sections.map((section) => resolveSection(content, section));
}

export function getOlympiadByRoute(
  content: GeniusContent,
  route: string
): OlympiadEntry | undefined {
  return content.olympiads.find((entry) => entry.route === route);
}
